import React, { useReducer, ReactNode } from "react";
import { useContextSelector, createContext } from "use-context-selector";

import { SocketMessageInterface } from "../../../interfaces/Monitoring.interface";
import { uuidToString } from "../../../utils/uuid";

interface State {
  byBank: { [key: string]: SocketMessageInterface };
  byLift: { [key: string]: SocketMessageInterface };
  bitmask: { [key: string]: boolean };
  positionMap: { [key: string]: number };
  serviceMap: { [key: string]: string };
  last?: SocketMessageInterface;
}

type Action =
  | { type: "UPDATE_MESSAGE"; payload: SocketMessageInterface }
  | { type: "CLEAR_MESSAGES" };

const BankMessagesContext = createContext<State | undefined>(undefined);
const BankMessagesDispatch = createContext<React.Dispatch<Action> | undefined>(
  undefined
);

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "UPDATE_MESSAGE": {
      const msg = action.payload;
      const bankKey = uuidToString(msg.bankId);
      const liftKey = uuidToString(msg.liftId);
      return {
        byBank: { ...state.byBank, [bankKey]: msg },
        byLift: { ...state.byLift, [liftKey]: msg },
        bitmask: { ...state.bitmask, ...msg.bitmask },
        positionMap: { ...state.positionMap, ...msg.positionMap },
        serviceMap: { ...state.serviceMap, ...msg.serviceMap },
        last: msg,
      };
    }
    case "CLEAR_MESSAGES": {
      return {
        byBank: {},
        byLift: {},
        bitmask: {},
        positionMap: {},
        serviceMap: {},
        last: undefined,
      };
    }
    default:
      return state;
  }
}

export const BankMessagesProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(reducer, {
    byBank: {},
    byLift: {},
    bitmask: {},
    positionMap: {},
    serviceMap: {},
    last: undefined,
  });

  return (
    <BankMessagesContext.Provider value={state}>
      <BankMessagesDispatch.Provider value={dispatch}>
        {children}
      </BankMessagesDispatch.Provider>
    </BankMessagesContext.Provider>
  );
};

export function useLastBankMessage(
  bankId: string
): SocketMessageInterface | undefined {
  return useContextSelector(BankMessagesContext, (ctx) => ctx?.byBank[bankId]);
}

export function useLastLiftMessage(
  liftId: string
): SocketMessageInterface | undefined {
  return useContextSelector(BankMessagesContext, (ctx) => ctx?.byLift[liftId]);
}

export function useAllLiftMessages(): {
  [key: string]: SocketMessageInterface;
} {
  return useContextSelector(BankMessagesContext, (ctx) => ctx?.byLift) || {};
}

export function useLastSocketMessage(): SocketMessageInterface | undefined {
  return useContextSelector(BankMessagesContext, (ctx) => ctx?.last);
}

export function useBankDispatch() {
  return useContextSelector(BankMessagesDispatch, (dispatch) => {
    if (!dispatch)
      throw new Error(
        "useBankDispatch must be used within BankMessagesProvider"
      );
    return dispatch;
  });
}

export function useBitmask(): { [key: string]: boolean } {
  return useContextSelector(BankMessagesContext, (ctx) => ctx?.bitmask) || {};
}

export function usePositionMap(): { [key: string]: number } {
  return (
    useContextSelector(BankMessagesContext, (ctx) => ctx?.positionMap) || {}
  );
}

export function useServiceMap(): { [key: string]: string } {
  return (
    useContextSelector(BankMessagesContext, (ctx) => ctx?.serviceMap) || {}
  );
}

export function useClearBankMessages() {
  const dispatch = useBankDispatch();
  return () => dispatch({ type: "CLEAR_MESSAGES" });
}
